/**
 * GN — ritaglio preciso della grafica dalle foto prodotto.
 * Le foto delle stampe sono il poster con un filo nero su fondo trasparente. Il bordo esterno si legge dalla
 * trasparenza (affidabile qualunque sia il disegno), poi si toglie lo spessore del filo (2,5% della larghezza
 * del poster, più un pixel di margine per l'antialias). Il risultato si adatta al riquadro senza deformare.
 * Usato da gn-wall-builder.js e, per i banner, su ogni <img data-gn-crop> dentro un riquadro con overflow:hidden.
 */
const DEFAULT = { x0: 0.124, y0: 0.0733, x1: 0.876, y1: 0.9267 }; // schema delle foto GN, se la lettura non riesce
const cache = new Map();

// La lettura avviene su una copia piccola e separata (CORS): l'immagine mostrata non usa crossorigin, così
// si carica sempre (Safari scarta le immagini crossorigin se il server non risponde come si aspetta).
// Sempre in https: su una pagina http l'indirizzo "//dominio" diventa http, il CDN risponde con un redirect
// e Safari blocca i redirect cross-origin (la stampa orizzontale restava in una cornice verticale).
const probeUrl = (src) => {
  const url = src.replace(/^(https?:)?\/\//, 'https://');
  return /[?&]width=\d+/.test(url) ? url.replace(/([?&]width=)\d+/, '$1240') : `${url}${url.includes('?') ? '&' : '?'}width=240`;
};

export function cropOf(src) {
  if (cache.has(src)) return cache.get(src);
  const job = new Promise((resolve) => {
    // Lettura non riuscita (rete, CORS): schema standard per ora, ma non si memorizza, così al giro dopo si riprova
    const failed = () => { cache.delete(src); resolve(DEFAULT); };
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const w = 240, h = Math.round((img.naturalHeight / img.naturalWidth) * w);
        const c = document.createElement('canvas'); c.width = w; c.height = h;
        const ctx = c.getContext('2d', { willReadFrequently: true }); ctx.drawImage(img, 0, 0, w, h);
        const d = ctx.getImageData(0, 0, w, h).data;
        let x0 = w, y0 = h, x1 = -1, y1 = -1;
        for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
          if (d[(y * w + x) * 4 + 3] > 128) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
        }
        // Nessuna trasparenza (foto su fondo pieno): si usa lo schema standard
        if (x1 < 0 || (x0 === 0 && y0 === 0 && x1 === w - 1 && y1 === h - 1)) return resolve(DEFAULT);
        const t = (x1 - x0 + 1) * 0.025 + 1;
        resolve({ x0: (x0 + t) / w, y0: (y0 + t) / h, x1: (x1 + 1 - t) / w, y1: (y1 + 1 - t) / h });
      } catch { failed(); } // immagine senza CORS: schema standard
    };
    img.onerror = failed;
    img.src = probeUrl(src);
  });
  cache.set(src, job);
  return job;
}

/** Proporzione larghezza/altezza della grafica ritagliata (>1 = orizzontale). */
export const ratioOf = (crop, naturalRatio) => ((crop.x1 - crop.x0) / (crop.y1 - crop.y0)) * naturalRatio;

/** Posiziona l'immagine dentro il riquadro (box W×H px) coprendolo con la sola grafica, senza deformarla. */
export function place(img, crop, W, H) {
  const nr = img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 0.8;
  const iw = crop.x1 - crop.x0, ih = (crop.y1 - crop.y0) / nr; // area grafica in unità di larghezza immagine
  const k = Math.max(W / iw, H / ih); // px per unità di larghezza
  const width = k, height = k / nr;
  img.style.cssText += `position:absolute;max-width:none;object-fit:fill;width:${width}px;height:${height}px;left:${(W - iw * k) / 2 - crop.x0 * k}px;top:${(H - ih * k) / 2 - crop.y0 * height}px`;
}

/**
 * Applica il ritaglio a ogni img[data-gn-crop] (banner): il genitore è il riquadro.
 * Riquadro con data-gn-swap e stampa orizzontale (es. La danza): il riquadro si gira, bordo destro fermo.
 */
export function cropAll(root = document) {
  root.querySelectorAll('img[data-gn-crop]').forEach(async (img) => {
    if (!img.complete) await new Promise((r) => img.addEventListener('load', r, { once: true }));
    const crop = await cropOf(img.currentSrc || img.src);
    const box = img.parentElement;
    const P = box.parentElement;
    if (box.hasAttribute('data-gn-swap') && ratioOf(crop, img.naturalWidth / img.naturalHeight) > 1 && box.offsetHeight > box.offsetWidth) {
      const W = P.clientWidth, H = P.clientHeight, w = box.offsetWidth, h = box.offsetHeight;
      Object.assign(box.style, {
        width: `${(h / W) * 100}%`,
        height: `${(w / H) * 100}%`,
        left: `${((box.offsetLeft + w - h) / W) * 100}%`,
        top: `${((box.offsetTop + h / 2 - w / 2) / H) * 100}%`,
      });
    }
    const fit = () => place(img, crop, box.clientWidth, box.clientHeight);
    fit();
    new ResizeObserver(fit).observe(box);
  });
}

if (document.querySelector('img[data-gn-crop]')) cropAll();
